import { useMemo } from 'react';

import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { get } from '@/helper/request';
import useRestQuery from '@/hooks/useRestQuery';
import { TagAvailableOnEnum } from '@/types/schema';

import {
  ReportTagUnionFragment,
  useReportTagsQuery,
} from '../../graphql/query/generated/reportTags';
import { ReportMetadataResponse } from './types';
import { getComputedReportMetadata } from './utils';

interface Props {
  dynamicFields?: boolean;
}

const EMPTY_ARRAY: never[] = [];
const API_PATH = '/api/report/metadata';

const useReportMetadata = ({ dynamicFields = false }: Props = {}) => {
  const { [FeatureFlags.EnableOKR]: isOKREnabled } = useFeatureFlags();

  const path = `${API_PATH}?tags=false`;
  const { isLoading, data } = useRestQuery<ReportMetadataResponse>(path, () => get(path), {
    refetchOnWindowFocus: false,
  });

  const [{ data: tagsData, fetching: tagsFetching }] = useReportTagsQuery({
    variables: {
      filters: {
        availableOn: [
          TagAvailableOnEnum.Content,
          TagAvailableOnEnum.Event,
          TagAvailableOnEnum.User,
          TagAvailableOnEnum.Okr,
          TagAvailableOnEnum.Consumption,
        ],
      },
      first: -1,
    },
    pause: !dynamicFields,
  });

  const reportAttributes = useMemo(() => {
    if (!data || data.error) {
      return null;
    }

    const tags = (tagsData?.tags?.edges?.map(({ node }) => node) ||
      EMPTY_ARRAY) as ReportTagUnionFragment[];

    return getComputedReportMetadata({ response: data, isOKREnabled, tags });
  }, [data, isOKREnabled, tagsData?.tags?.edges]);

  let error =
    !reportAttributes && !isLoading ? new Error('[ReportMetadata] No reportAttributes') : undefined;

  return { isLoading: isLoading || tagsFetching, reportAttributes, error };
};

export default useReportMetadata;
